/**
 * KerryYoo Extended Header
 */
(function () {
  'use strict';

  const BANNER_SECTION_TYPES = [
    'slideshow',
    'hero-video',
    'image-text-banner',
    'image-slideshow',
    'hero',
    'video',
    'extended-banner',
  ];

  const BANNER_CLASS_PATTERNS = [
    'section-slideshow',
    'section-hero',
    'section-image-text-banner',
    'section-image-slideshow',
    'section-hero-video',
    'extended-banner',
    'extended-banner-section',
  ];

  function initHeader(header) {
    if (!header || header.dataset.ehInitialized === 'true') return;
    header.dataset.ehInitialized = 'true';

    const stickyEnabled = header.dataset.sticky === 'true';
    const bannerTypes = (header.dataset.bannerTypes || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    detectPageBanner(header, bannerTypes);
    initMarquee(header);
    initSticky(header, stickyEnabled);
    initMegaMenus(header);
    initMobileDrawer(header);
    observeHeaderHeight(header);
    window.addEventListener(
      'resize',
      debounce(() => setHeaderHeightVar(header), 100),
      { passive: true }
    );
  }

  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), delay);
    };
  }

  function setHeaderHeightVar(header) {
    const height = header.offsetHeight;
    const topBar = header.querySelector('.extended-header__top');
    const topHeight = topBar ? topBar.offsetHeight : 0;

    document.documentElement.style.setProperty('--extended-header-height', `${height}px`);
    document.documentElement.style.setProperty('--header-height', `${height}px`);
    document.documentElement.style.setProperty('--header-section-height', `${height}px`);
    document.documentElement.style.setProperty('--announcement-height', `${topHeight}px`);
    document.documentElement.style.setProperty('--utility-height', '0px');
    document.documentElement.style.setProperty(
      '--header-is-sticky',
      header.classList.contains('is-sticky') ? '1' : '0'
    );
  }

  function observeHeaderHeight(header) {
    setHeaderHeightVar(header);
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => setHeaderHeightVar(header));
    observer.observe(header);
  }

  function sectionIsBanner(section, types) {
    if (!section) return false;

    const typedEl = section.querySelector('[data-section-type]');
    const sectionType =
      typedEl?.getAttribute('data-section-type') ||
      section.getAttribute('data-section-type') ||
      '';
    const className = section.className || '';
    const innerClass = typedEl?.className || '';

    return (
      types.some((t) => sectionType.includes(t)) ||
      BANNER_CLASS_PATTERNS.some((p) => className.includes(p) || innerClass.includes(p)) ||
      !!section.querySelector(
        '[data-extended-banner], .extended-banner, .extended-banner-section, [data-section-type="extended-banner"]'
      ) ||
      !!section.querySelector(
        '[class*="section-slideshow"], [class*="section-hero"], [class*="section-image-text-banner"]'
      )
    );
  }

  function pageHasBanner(extraTypes) {
    const types = [...BANNER_SECTION_TYPES, ...extraTypes];
    const main = document.querySelector('.main');
    const firstSection = main?.querySelector('.shopify-section:first-child');
    return sectionIsBanner(firstSection, types);
  }

  function applyBannerState(header, hasBanner, extraTypes) {
    if (hasBanner) {
      header.classList.remove('extended-header--solid');
      document.body.classList.add('has-page-banner');
      document.documentElement.setAttribute('data-eh-page-banner', 'true');

      const topBar = header.querySelector('.extended-header__top');
      const threshold = topBar ? topBar.offsetHeight : 10;
      if (window.scrollY <= threshold) {
        header.classList.remove('is-sticky-active');
      }
    } else {
      header.classList.add('extended-header--solid');
      document.body.classList.remove('has-page-banner');
      document.documentElement.removeAttribute('data-eh-page-banner');
    }

    header.dataset.hasBanner = hasBanner ? 'true' : 'false';
    setHeaderHeightVar(header);

    if (typeof theme !== 'undefined' && typeof theme.setHeaderHeightVars === 'function') {
      theme.setHeaderHeightVars();
    }
  }

  function detectPageBanner(header, extraTypes) {
    applyBannerState(header, pageHasBanner(extraTypes), extraTypes);
  }

  let refreshTimer = null;

  function refreshAllHeaders() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => {
      document.querySelectorAll('[data-extended-header]').forEach((header) => {
        const bannerTypes = (header.dataset.bannerTypes || '')
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);
        detectPageBanner(header, bannerTypes);
      });
    }, 80);
  }

  function initMarquee(header) {
    const marquee = header.querySelector('[data-eh-marquee]');
    if (!marquee || marquee.dataset.ehMarqueeReady === 'true') return;

    const track = marquee.querySelector('.extended-header__marquee-track');
    if (!track) return;

    const speed = parseFloat(header.dataset.tickerSpeed) || 30;
    const inner = document.createElement('div');
    inner.className = 'extended-header__marquee-inner';

    const clone = track.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');

    track.parentNode.insertBefore(inner, track);
    inner.appendChild(track);
    inner.appendChild(clone);

    marquee.dataset.ehMarqueeReady = 'true';

    const contentWidth = inner.scrollWidth / 2;
    const duration = Math.max(10, contentWidth / speed);
    marquee.style.setProperty('--eh-marquee-duration', `${duration}s`);
  }

  function initSticky(header, stickyEnabled) {
    if (!stickyEnabled) return;

    const nav = header.querySelector('.extended-header__nav');
    if (!nav) return;

    const threshold = header.querySelector('.extended-header__top')
      ? header.querySelector('.extended-header__top').offsetHeight
      : 10;

    const onScroll = () => {
      const wasSticky = header.classList.contains('is-sticky-active');
      const hasBanner = header.dataset.hasBanner === 'true';

      if (window.scrollY > threshold) {
        header.classList.add('is-sticky-active');
      } else {
        header.classList.remove('is-sticky-active');
      }

      if (hasBanner && window.scrollY <= threshold) {
        header.classList.remove('is-sticky-active');
      }

      if (wasSticky !== header.classList.contains('is-sticky-active')) {
        setHeaderHeightVar(header);
        if (typeof theme !== 'undefined' && typeof theme.setHeaderHeightVars === 'function') {
          theme.setHeaderHeightVars();
        }
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  function initMegaMenus(header) {
    const items = header.querySelectorAll('[data-extended-mega-item]');
    if (!items.length) return;

    let closeTimer = null;

    const closeAll = () => {
      items.forEach((item) => {
        item.classList.remove('is-mega-open');
        const trigger = item.querySelector('.is-mega-trigger');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    };

    items.forEach((item) => {
      const trigger = item.querySelector('.is-mega-trigger');
      const mega = item.querySelector('[data-extended-mega]');
      if (!trigger || !mega) return;

      const positionMega = () => {
        const headerRect = header.getBoundingClientRect();
        mega.style.top = `${headerRect.bottom}px`;
      };

      const open = () => {
        if (closeTimer) {
          clearTimeout(closeTimer);
          closeTimer = null;
        }
        closeAll();
        positionMega();
        item.classList.add('is-mega-open');
        trigger.setAttribute('aria-expanded', 'true');
      };

      const scheduleClose = () => {
        closeTimer = setTimeout(closeAll, 180);
      };

      item.addEventListener('mouseenter', open);
      item.addEventListener('mouseleave', scheduleClose);
      mega.addEventListener('mouseenter', open);
      mega.addEventListener('mouseleave', scheduleClose);

      trigger.addEventListener('focus', open);
    });

    window.addEventListener(
      'resize',
      debounce(() => {
        const openItem = header.querySelector('[data-extended-mega-item].is-mega-open');
        if (!openItem) return;
        const mega = openItem.querySelector('[data-extended-mega]');
        if (mega) {
          mega.style.top = `${header.getBoundingClientRect().bottom}px`;
        }
      }, 100),
      { passive: true }
    );

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeAll();
    });
  }

  function initMobileDrawer(header) {
    const toggle = header.querySelector('[data-eh-mobile-toggle]');
    const drawer = header.querySelector('[data-eh-drawer]');
    const overlay = header.querySelector('[data-eh-drawer-overlay]');
    const closeBtn = header.querySelector('[data-eh-drawer-close]');

    if (!toggle || !drawer || !overlay) return;

    const open = () => {
      drawer.classList.add('is-open');
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      drawer.classList.remove('is-open');
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    };

    toggle.addEventListener('click', open);
    overlay.addEventListener('click', close);
    if (closeBtn) closeBtn.addEventListener('click', close);
  }

  function initThemeHeaderHelpers() {
    if (typeof theme === 'undefined') return;
    if (typeof theme.setHeaderHeightVars === 'function') theme.setHeaderHeightVars();
    if (typeof theme.setUpHeaderResizeObservers === 'function') theme.setUpHeaderResizeObservers();
  }

  function initAll() {
    document.querySelectorAll('[data-extended-header]').forEach(initHeader);
    initThemeHeaderHelpers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.addEventListener('load', () => {
    refreshAllHeaders();
  });

  document.addEventListener('shopify:section:load', (event) => {
    const header = event.target.querySelector('[data-extended-header]');
    if (header) {
      initHeader(header);
      initThemeHeaderHelpers();
    } else {
      refreshAllHeaders();
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!event.target.querySelector?.('[data-extended-banner]')) {
      refreshAllHeaders();
      return;
    }
    document.documentElement.removeAttribute('data-eh-page-banner');
    refreshAllHeaders();
  });

  document.addEventListener('shopify:section:reorder', () => {
    refreshAllHeaders();
  });

  document.addEventListener('extended-banner:loaded', refreshAllHeaders);
})();
